import Button from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';

import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { State } from 'model/State';
import { Dimensions } from 'components/visuals';
import config from 'appconfig';

import {
  getCurrentMACContactDetails,
  selectAddNewPartnerContactSuccess,
  selectContactsForOrganisation,
  selectDeleteContactSuccess,
  selectEnableDisableMainContact,
} from './duck/selectors';
import {
  fetchContactsForOrganisation,
  fetchDeleteContact,
  fetchEnableDisableMainContact,
} from './duck/actions/contact';
import { messages } from './messages';
import MACPageHeader from './components/macPageHeader';
import { StyledPipeSymbol } from './components/licensesExpandedRow/StyledComponents';
import { AddPartnerContactModal } from './components/AddPartnerContactModal';
import { PageH3Header, StyledNoLeftPaddingButton, StyledSpan } from './components/styledComponents';
import EmailConfigurationSection from './components/EmailConfigurationSection';

const PartnerPageSection = styled.section`
  padding-top: ${Dimensions.units(2.5)}px;
`;

const TableRow = styled.tr`
  border-bottom: ${Dimensions.units(0.25)}px solid #dfe1e6;
`;

const TableHeader = styled.th<{ $padding?: string }>`
  padding: ${({ $padding }) => $padding || `${Dimensions.units(1)}px`};
`;

const TableData = styled.td`
  padding: ${Dimensions.units(1)}px;
`;

export const PartnersPage = () => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const userDetails = useSelector(getCurrentMACContactDetails)?.ifSuccess((r) => r);
  const contactsForOrganisation = useSelector((state: State) =>
    selectContactsForOrganisation(state, userDetails?.organisationDetails?.id),
  );
  const mainContactEnabledOrDisabled = useSelector(selectEnableDisableMainContact);
  const deleteContactResponse = useSelector(selectDeleteContactSuccess);
  const addNewPartnerContactSuccessResponse = useSelector(selectAddNewPartnerContactSuccess);

  useEffect(() => {
    if (!contactsForOrganisation) {
      dispatch(fetchContactsForOrganisation(userDetails?.organisationDetails?.id));
    }
  }, [userDetails]);

  useEffect(() => {
    if (mainContactEnabledOrDisabled === '') {
      dispatch(fetchContactsForOrganisation(userDetails?.organisationDetails?.id));
    }
  }, [mainContactEnabledOrDisabled]);

  useEffect(() => {
    if (deleteContactResponse === '') {
      dispatch(fetchContactsForOrganisation(userDetails?.organisationDetails?.id));
    }
  }, [deleteContactResponse]);

  useEffect(() => {
    if (addNewPartnerContactSuccessResponse) {
      dispatch(fetchContactsForOrganisation(userDetails?.organisationDetails?.id));
    }
  }, [addNewPartnerContactSuccessResponse]);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const userOrgDetails = userDetails?.organisationDetails;

  return userDetails && userOrgDetails && !userOrgDetails.expert ? (
    window.location.replace(config.profileAction)
  ) : (
    <>
      <MACPageHeader />
      <Tabs id="partner-page-tabs">
        <TabList>
          <Tab>
            <FormattedMessage {...messages.partnerProfileTab} />
          </Tab>
          <Tab>
            <FormattedMessage {...messages.emailConfigurationTab} />
          </Tab>
        </TabList>
        <TabPanel>
          <div>
            <PageH3Header>{userOrgDetails?.name}</PageH3Header>
            <PartnerPageSection>
              {userOrgDetails?.address1 && <div>{userOrgDetails?.address1}</div>}
              {userOrgDetails?.address2 && <div>{userOrgDetails?.address2}</div>}
              {(userOrgDetails?.city || userOrgDetails?.state || userOrgDetails?.postcode) && (
                <div>
                  {userOrgDetails?.city && (
                    <StyledSpan $margin="0 5px 0 0">{userOrgDetails?.city}</StyledSpan>
                  )}
                  {userOrgDetails?.state && (
                    <StyledSpan $margin="0 5px 0 0">{userOrgDetails?.state}</StyledSpan>
                  )}
                  {userOrgDetails?.postcode && (
                    <StyledSpan $margin="0 5px 0 0">{userOrgDetails?.postcode}</StyledSpan>
                  )}
                  {userOrgDetails?.country && <div>{userOrgDetails?.country}</div>}
                </div>
              )}
            </PartnerPageSection>
            <PartnerPageSection>
              <PageH3Header>
                <FormattedMessage {...messages.discountTitle} />
              </PageH3Header>
              <SectionMessage appearance="information">
                <FormattedMessage {...messages.partnerDiscountBanner} />
              </SectionMessage>
              {userOrgDetails?.discounts && (
                <table data-cy="discounts-table">
                  <thead>
                    <TableRow>
                      <TableHeader scope="col">
                        <FormattedMessage {...messages.discountDescription} />
                      </TableHeader>
                      <TableHeader scope="col">
                        <FormattedMessage {...messages.discountRate} />
                      </TableHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {userOrgDetails?.discounts.map((discount) => (
                      <TableRow key={userOrgDetails?.discounts.key}>
                        <TableData>{discount.description}</TableData>
                        <TableData>{discount.rate}%</TableData>
                      </TableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </PartnerPageSection>
            <PartnerPageSection>
              <PageH3Header>
                <FormattedMessage {...messages.partnerContacts} />
              </PageH3Header>
              <StyledNoLeftPaddingButton
                $paddingLeft={0}
                appearance="link"
                onClick={openModal}
                data-cy="add-partner-contact-btn"
              >
                <FormattedMessage {...messages.partnerAddContact} />
              </StyledNoLeftPaddingButton>
              {contactsForOrganisation ? (
                <table data-cy="contacts-table">
                  <thead>
                    <TableRow>
                      <TableHeader scope="col">
                        <FormattedMessage {...messages.contactsTableContact} />
                      </TableHeader>
                      <TableHeader scope="col">
                        <FormattedMessage {...messages.contactsTableEmail} />
                      </TableHeader>
                      <TableHeader scope="col">
                        <FormattedMessage {...messages.contactsTableContactType} />
                      </TableHeader>
                      <TableHeader $padding="8px 8px 8px 18px" scope="col">
                        <FormattedMessage {...messages.contactsTableOperations} />
                      </TableHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {contactsForOrganisation?.map((contact) => {
                      return (
                        <TableRow
                          data-cy={`contacts-table-row=${contact.contactDetails.id}`}
                          key={contact.contactDetails.id}
                        >
                          <TableData>{contact.contactDetails.displayName}</TableData>
                          <TableData>{contact.contactDetails.email}</TableData>
                          <TableData>
                            {contact.contactDetails.mainContact ? (
                              <strong>
                                <FormattedMessage {...messages.contactsMain} />
                              </strong>
                            ) : (
                              <FormattedMessage {...messages.contactsSecondary} />
                            )}
                          </TableData>
                          <TableData>
                            <div>
                              <Button
                                appearance="link"
                                onClick={() =>
                                  dispatch(
                                    fetchEnableDisableMainContact(
                                      contact.contactDetails.id,
                                      !contact.contactDetails.mainContact,
                                    ),
                                  )
                                }
                              >
                                {contact.contactDetails.mainContact ? (
                                  <FormattedMessage {...messages.makeSecondary} />
                                ) : (
                                  <FormattedMessage {...messages.makeMain} />
                                )}
                              </Button>
                              <StyledPipeSymbol>|</StyledPipeSymbol>
                              <Button
                                data-cy="remove-contact-btn"
                                appearance="link"
                                onClick={() =>
                                  dispatch(fetchDeleteContact(contact.contactDetails.id))
                                }
                              >
                                <FormattedMessage {...messages.contactsRemove} />
                              </Button>
                            </div>
                          </TableData>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div>
                  <Spinner />
                </div>
              )}
            </PartnerPageSection>
            <br />
            <FormattedMessage {...messages.contactsTableSubText} />
          </div>
        </TabPanel>
        <TabPanel>
          <EmailConfigurationSection partnerId={userDetails?.organisationDetails?.id} />
        </TabPanel>
      </Tabs>
      <AddPartnerContactModal
        isOpen={isOpen}
        closeModal={closeModal}
        switchedUserDetails={userDetails}
      />
    </>
  );
};
